import { PutObjectCommand, S3Client } from "@aws-sdk/client-s3";
import * as Sentry from "@sentry/browser";
import {
  API_URL,
  ASSESSMENT_STATUSES,
  AWS_CONFIG,
  DL_MODELS,
  S3_BUCKET,
} from "../movenet/params";
import { SendExceptionEmail } from "../services/ExercisesService";
import { getPatientAge } from "../utils/sharedFunction";
import { config } from "./env_config";
import { TUGAssessmentStates, TugAudios } from "./assessment_states";
import { TugAudioPlayer } from "./audio-player";
import { calculateFallRisk } from "./gait-util";
import { MultiPoseUtility } from "./multipose_utility";
import { PatientSelection } from "./patient_selection";
import { SentryLogging } from "./sending_sentry_logs";
import { SinglePoseUtil } from "./single_pose_utility";
import { GestureDetector } from "./start_gesture_detector";
import { StepDetector } from "./step-detector";
import {
  stopiOSVoiceListening,
  startiOSVoiceListening,
} from "../utils/mobile-bridge";
import { SitStandProcessing } from "./sitStandProcessing";
import { MoveNetPoseWrapper } from "./pose-wrapper";
import shoePrintSrc from "../assets/images/shoe_print.png";

const FEET_BOX_LENGTH = 120;
const cv = window.cv;
const s3Client = new S3Client(AWS_CONFIG);

/**
 * Represents foot data in the Bird's Eye View coordinate system.
 * Contains timestamps, foot positions, and slope data (if used).
 */
export class FeetData {
  /**
   * @param {object} left_ankle_bev - The x,y point for the left ankle in Bird's Eye View.
   * @param {object} right_ankle_bev - The x,y point for the right ankle in Bird's Eye View.
   */
  constructor(left_ankle_bev, right_ankle_bev) {
    this.timestamp = Date.now();
    this.elapsedTime = 0;
    this.left_foot_bev = left_ankle_bev;
    this.right_foot_bev = right_ankle_bev;
    this.right_foot_bev_slope = 0;
    this.left_foot_bev_slope = 0;
  }
}

/**
 * The Tug class orchestrates the logic for a walking or tug assessment.
 * It integrates hand-gesture detection, foot tracking, distance calibration,
 * start/stop line detection, and generation of final results.
 */
export class TUG {
  /**
   * Constructs a new Tug instance.
   * @param {object} calibration - Calibration object responsible for computing Bird's Eye View transformations.
   * @param {object} camera - An object that wraps around video/canvas functionalities.
   * @param {function} switch_to_single_pose - Callback that switches the pose detection model to BlazePose (single-pose).
   * @param {function} reset_pose_estimator - Resets the pose estimator to capture full-sized frames.
   */
  constructor(
    calibration,
    camera,
    switch_to_single_pose,
    reset_pose_estimator,
  ) {
    this.switch_to_single_pose_callback = switch_to_single_pose;
    this.reset_pose_estimator = reset_pose_estimator;
    this.sit_stand_processing = new SitStandProcessing();
    this.states = [
      "Calibration start",
      "Calibration complete",
      "Ready to start test",
      "Start gesture detected",
      "Test in progress",
      "Test complete",
    ];
    this.current_state = TUGAssessmentStates.calculatingDepth;
    this.calibration = calibration;
    this.gesture_detector = new GestureDetector();
    this.camera = camera;
    this.left_boundary = 0;
    this.right_boundary = 0;
    this.shoulder_differernce = 0;
    this.patient_id = -1;
    this.therapist_id = -1;
    this.frames_with_no_patient = 0;
    this.start_time = 0;
    this.end_time = 0;
    this.calibration_error_start_time = 0;
    this.calibration_error_time = 0;
    this.total_calibration_error_time = 0;
    this.calibration_error_occured = false;
    this.two_second_behind_the_point_start = 0;
    this.two_second_behind_the_point_end = 0;
    this.show_raise_hand_modal = true;
    this.first_frame = true;
    this.notEnoughSpace = false;
    this.notEnoughSpaceClicked = false;
    this.audio_player = new TugAudioPlayer();
    this.display_start_assessment_modal = true;
    this.pathwayBlocked = true;
    this.withInBoxAudio = true;
    this.pathwayCalibrated = true;
    this.is_path_clear = true;
    this.enabled_voice_listener = false;
    this.voice_command_received = false;

    this.voiceCommandListener = (event) => {
      const command = event.detail.command;
      console.log("🎤 [TUG] Received voice command:", command);
      if (command === "START") {
        console.log("Voice Triggered START command");
        if (this.enabled_voice_listener && !this.voice_command_received) {
          this.voice_command_received = true;
          this.enabled_voice_listener = false;
          stopiOSVoiceListening();
        }
      }
    };
    window.addEventListener("voice-command", this.voiceCommandListener);

    this._onPageHide = () => {
      try {
        if (this.voiceCommandListener) {
          window.removeEventListener(
            "voice-command",
            this.voiceCommandListener,
          );
          this.voiceCommandListener = null;
        }
      } catch (e) {}
    };
    window.addEventListener("pagehide", this._onPageHide);

    this.patient_in_box_counter = 0;
    this.ankle_locations_bev = [
      [
        "Timestamp",
        "left_foot_bev_x",
        "left_foot_bev_y",
        "right_foot_bev_x",
        "right_foot_bev_y",
        "left_foot_cv_x",
        "left_foot_cv_y",
        "right_foot_cv_x",
        "right_foot_cv_y",
        "left_ankle_cv_x",
        "left_ankle_cv_y",
        "right_ankle_cv_x",
        "right_ankle_cv_y",
        "left_foot_score",
        "right_foot_score",
        "left_ankle_score",
        "right_ankle_score",
      ],
    ];
    this.step_detector = new StepDetector();

    // Offscreen canvas for capturing frames to pass to the depth model
    let canvas = document.createElement("canvas");
    canvas.width = 1080;
    canvas.height = 1920;
    this.offscreen_canvas = canvas;
    this.offscreen_canvas_ctx = this.offscreen_canvas.getContext("2d", {
      willReadFrequently: true,
    });
    this.canvas = document.getElementById("output");
    this.ctx = this.canvas.getContext("2d");
    this.side_margin = 0.4;

    this.feet_locations = [];
    this.feet_locations_cam = [];
    this.LEFT_FOOT_IDX = 31;
    this.RIGHT_FOOT_IDX = 32;
    this.LEFT_ANKLE_IDX = 27;
    this.RIGHT_ANKLE_IDX = 28;
    this.LEFT_SHOULDER_IDX = 11;
    this.RIGHT_SHOULDER_IDX = 12;
    let videoWidth = 1080;
    let videoHeight = 1920;
    this.options = {
      sx: 0,
      sy: 0,
      sw: videoWidth,
      sh: videoHeight,
    };

    this.are_you_there_flag = true;
    this.are_you_there_timer = 0;
    this.two_seconds_timer_flag = true;
    this.hand_gesture_detected = false;
    this.cropped_image_length = 0;
    this.cropped_image_width = 0;
    this.left_shoulder_bound = 0;
    this.right_shoulder_bound = 0;
    this.shoulder_diff = 0;
    this.ready_to_start_test_flag = true;
    this.prev_index = 0;
    this.left_shoulder = 0;
    this.right_shoulder = 0;
    this.two_second_start_time = 0;
    this.is_everyone_behind_the_point_counter = 0;
    this.crossed_finish_line = false;
    this.callibration_audio_completion = false;
    this.start_hand_gesture_detection = false;
    this.start_hand_gesture_detection_counter = 0;
    this.shown_persistent_icon = false;
    this.finish_line_crossed = false;
    this.start_line_crossed = false;
    this.start_line_extended_crossed = false;
    this.prev_left_ankle_value = 0;
    this.prev_right_ankle_value = 0;
    this.prev_left_shoulder_value = { x: null, y: null };
    this.prev_right_shoulder_value = { x: null, y: null };
    this.flickerStatus = false;
    this.inactive_for_60_seconds = false;

    // Sentry / Logging
    let patientServerID = localStorage.getItem("patientServerID")
      ? localStorage.getItem("patientServerID")
      : "000";
    let userID = localStorage.getItem("UserID")
      ? localStorage.getItem("UserID")
      : "000";
    let businessCode = localStorage.getItem("BusinessCode")
      ? localStorage.getItem("BusinessCode")
      : "000";

    Sentry.setTag("UserID", userID);
    Sentry.setTag("Patient Server ID", patientServerID);
    Sentry.setTag("Business Code", businessCode);

    this.sentry_prefix = `TUG_` + new Date().toISOString();
    ASSESSMENT_STATUSES.sentry_message = this.sentry_prefix;
    ASSESSMENT_STATUSES.are_you_there = false;
    ASSESSMENT_STATUSES.incomplete_test = false;
    ASSESSMENT_STATUSES.assessment_Completed = false;
    ASSESSMENT_STATUSES.assessment_successful_completion = false;
    ASSESSMENT_STATUSES.retry = false;
    this.sentry_start_time = 0;

    // Reading are-you-there-timer & converting it to milliseconds
    this.are_you_there_modal_timer =
      config["are-you-there-timer-in-seconds"] * 1000;
    this.patient_feet_box_x = [];
    this.patient_feet_box_y = [];

    // Load shoe print image for bounding box
    this.shoe_print_img = new Image();
    this.shoe_print_img.src = shoePrintSrc;
    this.shoe_print_loaded = false;
    this.shoe_print_img.onload = () => {
      this.shoe_print_loaded = true;
    };

    this.is_captured = null;
    this.apicall = null;
    this.is_uploaded = null;
    this.audio_play = null;
    this.is_calibrated = null;
    this.isPatientNearStopLine = null;
    this.is_depth_calculated = null;
    this.is_api_called = null;
    this.current_y = 0;
    this.old_y = 0;
    this.pathBlockedCount = null;
    this.return_to_start_prompted = false;
    this.standInsidebox = true;
    this.return_start_time = 0;
    this.patient_seated_confirm_frames = 0;
    this.patient_seated_release_frames = 0;
    this.selected_patient_id = null;
    this.selected_patient_center_x = null;
    // Remote UP/DOWN patient selection: the participant the therapist selects before the start
    this.patient_selection = new PatientSelection();
  }

  /**
   * Safely stamps end_time once, used on all terminal paths and as a fallback.
   */
  markEndIfNeeded() {
    if (!this.end_time && this.start_time) {
      this.end_time = Date.now();
    }
  }

  /**
   * Main update function called on each animation frame. Manages the current state machine,
   * draws lines, checks for path obstruction, and handles logic for start/finish detection.
   * @param {Array} poses - The array of poses detected in the current frame.
   */
  async update(poses) {
    // The participant the therapist selected with the remote, while it can still be changed.
    // patient_id also makes movenet draw this person's skeleton green.
    if (this.is_patient_selection_open()) {
      const patient = this.patient_selection.update(poses);
      this.patient_id = patient ? patient.id : -1;
    }

    // Draw lines if calibration is completed
    // if (this.calibration.complete()) {
    //   if (this.is_start_line_crossed()) {
    //     this.camera.draw_line(this.calibration.start_line_cv, "green");
    //   } else {
    //     this.camera.draw_line(this.calibration.start_line_cv, "red");
    //   }

    //   if (this.is_finish_line_crossed()) {
    //     this.camera.draw_line(this.calibration.stop_line_cv, "green");
    //   } else {
    //     this.camera.draw_line(this.calibration.stop_line_cv, "red");
    //   }
    // }

    if (this.notEnoughSpace) {
      if (!this.notEnoughSpaceClicked) {
        document.getElementById("hdnNotEnoughSpaceModalPopup").click();
        this.notEnoughSpaceClicked = true;
        this.notEnoughSpace = false;
      }
      return;
    }

    // If the test is complete or in an interrupted state, exit early
    if (
      this.current_state === TUGAssessmentStates.test_complete ||
      ASSESSMENT_STATUSES.calibration_error_state ||
      this.current_state === TUGAssessmentStates.assessment_interrupted ||
      ASSESSMENT_STATUSES.exit_assessment_modal
    ) {
      // Stop voice listening on any termination condition
      if (this.enabled_voice_listener) {
        stopiOSVoiceListening();
        this.enabled_voice_listener = false;
      }
      if (
        ASSESSMENT_STATUSES.calibration_error_state &&
        this.current_state === TUGAssessmentStates.test_in_progress
      ) {
        this.calibration_error_occured = true;
        this.calibration_error_time =
          Date.now() - this.calibration_error_start_time;
      }

      // Ensure end_time is stamped on any terminal/exit path
      this.markEndIfNeeded();

      this.are_you_there_flag = true;
      this.are_you_there_timer = 0;
      return;
    }

    if (ASSESSMENT_STATUSES.retry) {
      this.current_state = TUGAssessmentStates.calculatingDepth;
      ASSESSMENT_STATUSES.retry = false;
    }
    // Check if no poses are detected -> triggers an "Are you there?" modal if idle for too long
    if (!poses || poses.length === 0) {
      if (this.are_you_there_flag) {
        this.are_you_there_flag = false;
        this.are_you_there_timer = Date.now();
      }
      let time_now = Date.now();
      const time_since_screen_inactive = time_now - this.are_you_there_timer;
      if (
        time_since_screen_inactive >= 60000 &&
        !this.inactive_for_60_seconds
      ) {
        this.inactive_for_60_seconds = true;
        this.append_sentry_message(
          "No pose/activity detected for more than 60 seconds.",
        );
      }
      if (time_since_screen_inactive >= this.are_you_there_modal_timer) {
        this.are_you_there_flag = true;
        this.are_you_there_timer = 0;
        document.getElementById("hdnAreYouTherePopup").click();
      }
    } else {
      this.are_you_there_flag = true;
      this.are_you_there_timer = 0;
    }

    if (document.readyState !== "complete") {
      return;
    }

    // Log the first frame, and optionally play an audio
    if (this.first_frame) {
      this.append_sentry_message("Assessment Started");
      if (DL_MODELS.MessageForSentry === "") {
        this.append_sentry_message("DL_Model already loaded");
      } else {
        this.append_sentry_message(DL_MODELS.MessageForSentry);
        DL_MODELS.MessageForSentry = "";
      }
      this.append_sentry_message(
        "Screen Resolution: " + this.canvas.width + "x" + this.canvas.height,
      );
      this.first_frame = false;
      this.camera.flip_horizontal();

      if (ASSESSMENT_STATUSES.debug_calibration) {
        ASSESSMENT_STATUSES.debug_calibration = false;
      }
    }

    if (ASSESSMENT_STATUSES.debug_calibration) {
      this.camera.video.pause();
    }

    console.log("this.current_state", this.current_state);

    try {
      switch (this.current_state) {
        // --------------------------- 1) ASSESSMENT STARTED ------------------------------

        case TUGAssessmentStates.PathwayCalibration:
          let elapsed_time0 = Date.now() - this.two_second_start_time;
          this.pathwayBlocked = true;
          if (elapsed_time0 <= 3500) return;
          this.current_state = TUGAssessmentStates.calculatingDepth;
          this.two_second_start_time = Date.now();
          break;

        case TUGAssessmentStates.calculatingDepth:
          // Check if the path is clear
          this.is_path_clear = this.path_checking(poses);

          this.calibration.on_complete_callback();
          this.get_start_extended_points();
          if (!this.is_api_called && this.is_path_clear) {
            this.audio_player.play(TugAudios.calibrating);
            this.uploadImageToS3AndCallAPI();
            this.is_api_called = true;
          }
          if (!this.calibration.depthData) this.draw_PathClearanceBox();

          if (
            this.pathwayCalibrated &&
            this.is_path_clear &&
            !ASSESSMENT_STATUSES.assessment_Completed &&
            !ASSESSMENT_STATUSES.calibration_error_state &&
            this.current_state !== TUGAssessmentStates.assessment_interrupted &&
            this.current_state !== TUGAssessmentStates.test_complete
          ) {
            const event = new CustomEvent("dontwalkmodal", {
              detail: this.calibration.percentDepthData,
            });
            document.getElementById("hdnGaitDontWalk").dispatchEvent(event);
          }

          // Path not clear -> path blocked error
          if (!this.is_path_clear && !this.calibration.depthData) {
            console.warn("Pathway blocked GaitPathwayBlocked : ");
            if (!this.pathBlockedCount) {
              this.pathBlockedCount = 0;
            }
            this.pathBlockedCount++;

            if (this.pathBlockedCount > 3) {
              // Stop voice listening on pathway blocked error
              if (this.enabled_voice_listener) {
                stopiOSVoiceListening();
                this.enabled_voice_listener = false;
              }
              this.current_state = TUGAssessmentStates.test_complete;
              document.getElementById("hdnRemoveGaitDontWalk")?.click();
              document.getElementById("hdnAssessementErrorPopup").click();
              ASSESSMENT_STATUSES.assessment_Completed = true;
              localStorage.removeItem("modalDisplayed");
              this.audio_player.play(TugAudios.assessment_interrupted);
              console.log("Assessment ERROR occured.");
              this.append_sentry_message(
                "Assessment ERROR occured - Pathway has been blocked.",
              );
              return;
            }

            if (this.pathwayBlocked) {
              this.pathwayBlocked = false;
              document.getElementById("hdnRemoveGaitDontWalk")?.click();
              document.getElementById("hdnGaitPathwayBlocked").click();
              if (this.audio_player.is_available()) {
                this.audio_player.play(TugAudios.pathway_blocked);
              }
              this.current_state = TUGAssessmentStates.PathwayCalibration;
              this.two_second_start_time = Date.now();
            }

            // Path is clear & depth data is fetched -> complete calibration
          } else if (this.calibration.depthData) {
            if (this.audio_player.is_available()) {
              if (this.pathwayCalibrated) {
                this.pathwayCalibrated = false;
                setTimeout(() => {
                  this.audio_player.play(TugAudios.calibrated);
                  document
                    .getElementById("hdnGaitCalibrationComplete")
                    ?.click();
                  this.is_depth_calculated = true;

                  setTimeout(() => {
                    this.current_state = TUGAssessmentStates.MoveInsidebox;
                  }, 2000);
                }, 3000);
              }
            }
          }

          break;

        case TUGAssessmentStates.MoveInsidebox:
          this.draw_PatientFeetBox(poses);
          this.pathwayCalibrated = true;
          this.pathwayBlocked = true;
          if (this.audio_player.is_available() && this.withInBoxAudio) {
            this.audio_player.play(
              TugAudios.calibration_complete_stand_within_box,
            );
            this.withInBoxAudio = false;
          }

          // Buffer variable -> 30% above & below the box's middle point
          let patient_inner_buffer = Math.abs(
            (this.patient_feet_box_y[0] - this.patient_feet_box_y[1]) * 0.2,
          );

          // Check if someone is in the box
          if (
            this.audio_player.is_available() &&
            MultiPoseUtility.is_someone_in_box(
              // With a remote selection, only the selected person counts; otherwise anyone in the box
              this.patient_selection.start_check_poses(poses),
              this.patient_feet_box_x,
              [
                this.patient_feet_box_y[0] + patient_inner_buffer,
                this.patient_feet_box_y[1] - patient_inner_buffer,
              ],
              this.camera,
            )
          ) {
            this.patient_in_box_counter++;
            if (this.patient_in_box_counter >= 5) {
              this.audio_player.play(TugAudios.place_chair_behind_patient);
              // if (!this.is_depth_calculated) {
              //   this.current_state = AssessmentStates.pathway_calibration_start;
              // } else {
              this.current_state = TUGAssessmentStates.SitToStart;
              // }
              // this.gesture_detector._screen_color_change();
              this.show_raise_hand_modal = true;
              // document.getElementById("hdnPersonDetectedModalPopup").click();
              // document.getElementById("hdnAssessmentStartUpMessage").click();
              document
                .getElementById("hdnTugChairBehindPatientMessage")
                .click();

              this.start_hand_gesture_detection = false;
              // console.log("Everyone is behind start point!");
              this.append_sentry_message("Everyone is behind start point!");
              this.two_second_start_time = Date.now();
            }
          } else {
            this.patient_in_box_counter = 0;
          }
          break;

        case TUGAssessmentStates.SitToStart:
          this.withInBoxAudio = true;
          this.draw_PatientFeetBox(poses);

          // With a remote selection, only the selected person counts; otherwise anyone in the box
          const sit_check_poses = this.patient_selection.start_check_poses(poses);
          let patient_in_box1 = MultiPoseUtility.is_someone_in_box(
            sit_check_poses,
            this.patient_feet_box_x,
            this.patient_feet_box_y,
            this.camera,
          );

          if (
            this.is_patient_in_box_and_sitting(sit_check_poses) &&
            this.audio_player.is_available()
          ) {
            this.audio_player.play(TugAudios.participant_detected);
            document.getElementById("hdnPersonDetectedModalPopup").click();
            this.current_state = TUGAssessmentStates.ready_to_start_test;
            this.two_second_start_time = Date.now();
          } else {
            if (!patient_in_box1 && this.audio_player.is_available()) {
              this.handleEveryoneBehindPoint();
            }
          }
          // }
          break;

        // --------------------------- 4) READY TO START TEST -----------------------------
        case TUGAssessmentStates.ready_to_start_test:
          this.draw_PatientFeetBox(poses);
          const elapsed_time1 = Date.now() - this.two_second_start_time;
          // With a remote selection, only the selected person counts; otherwise anyone in the box
          const ready_check_poses = this.patient_selection.start_check_poses(poses);
          let patient_in_box = MultiPoseUtility.is_someone_in_box(
            ready_check_poses,
            this.patient_feet_box_x,
            this.patient_feet_box_y,
            this.camera,
          );

          if (elapsed_time1 <= 2000) {
            console.log("we are in 2 second buffer");
            this.is_everyone_behind_the_point_counter = 0;
            return;
          }

          if (!patient_in_box && this.audio_player.is_available()) {
            this.handleEveryoneBehindPoint();
          }

          // Not once the remote's START was accepted (it may come before the prompt)
          if (
            this.show_raise_hand_modal &&
            this.audio_player.is_available() &&
            !this.voice_command_received
          ) {
            // Prompt user to raise arm
            this.show_raise_hand_modal = false;
            this.audio_player.play(TugAudios.raiseHand_to_start_and_turn_back);
            if (this.enabled_voice_listener) {
              stopiOSVoiceListening();
              this.enabled_voice_listener = false;
            }
            this.start_hand_gesture_detection_counter = 0;
            this.append_sentry_message("Hand Raised Modal is displayed.");
            document.getElementById("hdnRaiseArmModalPopup").click();
          }

          // Show persistent icon once audio finishes (one-shot, algorithm-driven)
          if (!this.shown_persistent_icon && !this.show_raise_hand_modal && this.audio_player.is_available()) {
            this.shown_persistent_icon = true;
            document.getElementById("hdnRaiseArmStopModalPopup")?.click();
          }

          // Record initial ankle location
          if (this.ready_to_start_test_flag) {
            this.gesture_detector.record_ankle_location(poses);
            this.ready_to_start_test_flag = false;
          }

          const personsInBox = MultiPoseUtility.get_poses_in_box(
            ready_check_poses,
            this.patient_feet_box_x,
            [this.patient_feet_box_y[0], this.patient_feet_box_y[1]],
            this.camera,
          );

          // Seated subset
          const seatedCandidates = personsInBox.filter((p) => {
            return this.is_person_sitting(p);
          });

          if (seatedCandidates)
            this.gesture_detector.detect_start_gesture(poses);

          // --- Debounced seated state ---
          if (seatedCandidates.length > 0) {
            this.patient_seated_confirm_frames++;
            this.patient_seated_release_frames = 0;
          } else {
            this.patient_seated_release_frames++;
            this.patient_seated_confirm_frames = 0;
          }

          const enableThreshold = 4;
          const disableThreshold = 20;

          if (
            !this.enabled_voice_listener &&
            !this.voice_command_received &&
            this.audio_player.is_available() &&
            this.patient_seated_confirm_frames >= enableThreshold
          ) {
            setTimeout(() => {
              startiOSVoiceListening();
            }, 250);
            this.enabled_voice_listener = true;
          }

          if (
            this.enabled_voice_listener &&
            this.patient_seated_release_frames >= disableThreshold
          ) {
            stopiOSVoiceListening();
            this.enabled_voice_listener = false;
          }

          // Continue to monitor gesture if voice or gesture flow is active
          // this.gesture_detector.detect_start_gesture(poses);
          if (
            this.gesture_detector.start_gesture_issued() ||
            this.voice_command_received
          ) {
            if (this.enabled_voice_listener) {
              stopiOSVoiceListening();
              this.enabled_voice_listener = false;
            }
            if (!this.audio_player.is_available()) this.audio_player.stop();
            document.getElementById("hdnHideHandRaisePersistentIcon")?.click();
            this.hand_gesture_detected = true;
            console.log("Start gesture issued!");
            this.append_sentry_message("Start (Hand raised) gesture issued!");
            this.append_sentry_message(
              "Number of persons in the frame while gesture detection: " +
                poses.length.toString(),
            );
          }

          if (this.hand_gesture_detected && this.calibration.depthData) {
            this.calibration.start_point = this.calibration.get_warped_point(
              this.calibration.start_point_cv,
            );
            this.calibration.stop_point = this.calibration.get_warped_point(
              this.calibration.stop_point_cv,
            );
            this.reset_pose_estimator();
            this.switch_to_blazepose(poses);
            this.gesture_detector.reset();
            this.current_state = TUGAssessmentStates.start_gesture_detected;
            this.two_second_start_time = Date.now();
            console.log("ready_to_start_test");
          }
          break;

        // --------------------------- 5) START GESTURE DETECTED --------------------------
        case TUGAssessmentStates.start_gesture_detected:
          console.log("start_gesture_detected");
          // this.draw_PatientFeetBox(poses);
          if (poses.length > 1) {
            // Wait if multiple poses are detected while switching to BlazePose
            break;
          }
          if (this.display_start_assessment_modal) {
            this.audio_player.play(TugAudios.go);
            document.getElementById("hdnCountDownGaitCompletePopup").click();
            this.display_start_assessment_modal = false;
          }

          if (!this.flickerStatus) this.flickerDetection(poses);

          if (this.is_patient_standing(poses) && !this.start_time) {
            this.start_time = Date.now();
          }

          // If the patient crosses the start line, move to "Test in progress"
          if (
            SinglePoseUtil.has_someone_crossed_start_line(
              poses,
              this.calibration,
            ) &&
            poses.length
          ) {
            this.start_line_crossed = true;
            this.setting_patient_region(poses);
            this.current_state = TUGAssessmentStates.test_in_progress;
            this.two_second_start_time = Date.now();

            console.log("Start line crossed!");
            this.append_sentry_message(
              "assessment started - Start line crossed!",
            );
            this.calibration_error_start_time = Date.now();
          }
          this.frames_with_no_patient = 0;
          break;

        // --------------------------- 6) TEST IN PROGRESS --------------------------------
        case TUGAssessmentStates.test_in_progress:
          this.draw_PatientFeetBox(poses);
          this.calibration_error_start_time = Date.now();
          if (this.calibration_error_occured) {
            this.calibration_error_occured = false;
            this.total_calibration_error_time =
              this.total_calibration_error_time + this.calibration_error_time;
          }
          if (!this.flickerStatus) this.flickerDetection(poses);

          // Check if the patient is near the finish line
          this.patient_near_stopline(poses);

          // Detect if a random crossing behind the patient
          if (
            this.bird_eye_view_patient_crossing(poses) &&
            !this.isPatientNearStopLine
          ) {
            // Error out if not near the stop line

            if (this.enabled_voice_listener) {
              stopiOSVoiceListening();
              this.enabled_voice_listener = false;
            }
            this.current_state = TUGAssessmentStates.test_complete;
            document.getElementById("hdnAssessementErrorPopup").click();
            ASSESSMENT_STATUSES.assessment_Completed = true;
            localStorage.removeItem("modalDisplayed");
            this.audio_player.play(TugAudios.assessment_interrupted);
            console.log("Assessment ERROR occured..");
            this.append_sentry_message(
              "Assessment ERROR occured - Someone has crossed the Patient during assessment..",
            );
            return;
          }

          // Record ankle positions for the entire test
          this.record_ankle_locations_BEV(poses);

          // Check if finishing line is crossed
          this.crossed_finish_line =
            SinglePoseUtil.has_someone_crossed_finish_line(
              poses,
              this.calibration,
            ) && poses.length;

          if (
            (this.bird_eye_view_patient_crossing(poses) ||
              this.second_person_crossing_detection(poses)) &&
            this.isPatientNearStopLine
          ) {
            // If random crossing but the patient is near the stop line, consider it completed
            this.crossed_finish_line = true;
          }

          // If patient is near end line/end bounding box, turn screen green as a cue
          if (this.isPatientNearStopLine) {
            this.gesture_detector._screen_color_change();
          }

          // If finish line is crossed, finalize the assessment
          if (this.crossed_finish_line && this.audio_player.is_available()) {
            this.finish_line_crossed = true;
            let feet_data = `${
              this.sentry_prefix
            } Feet location: ${JSON.stringify(this.feet_locations)}`;

            Sentry.captureMessage(
              "This message contains the Feet Location data.",
              {
                extra: {
                  full_message: feet_data,
                },
              },
            );

            this.current_state = TUGAssessmentStates.ReturnToStart;
            this.two_second_start_time = Date.now();

            // console.log("Finish line crossed!");

            this.append_sentry_message("Return State - Finish line crossed!");
          } else {
            // Reset if we didn't detect crossing
            this.frames_with_no_patient = 0;
          }
          break;

        case TUGAssessmentStates.ReturnToStart:
          const elapsed_time2 = Date.now() - this.two_second_start_time;

          if (this.has_patient_turned_and_moved(poses)) {
            this.gesture_detector._screen_color_change();
            return;
          }
          this.draw_PatientFeetBox(poses);

          // Prompt patient to return to start area (only once)
          if (!this.return_to_start_prompted) {
            // document.getElementById("hdnReturnToStartModalPopup").click();
            this.return_to_start_prompted = true;
            this.return_start_time = Date.now();
            this.append_sentry_message("Return to start area prompted.");
          }

          // Record ankle positions during return journey
          this.record_ankle_locations_BEV(poses);

          // Draw the start box for visual feedback
          // this.draw_PatientFeetBox(poses);

          // Check if patient is back in the start box
          let patient_in_start_box = SinglePoseUtil.is_someone_in_box(
            poses,
            this.patient_feet_box_x,
            this.patient_feet_box_y,
            this.camera,
          );

          // Check if patient is sitting
          let patient_is_sitting = this.is_patient_sitting(poses);

          if (patient_in_start_box && patient_is_sitting) {
            // Stop voice listening when patient leaves frame
            // if (this.enabled_voice_listener) {
            //   stopiOSVoiceListening();
            //   this.enabled_voice_listener = false;
            // }
            this.audio_player.play(TugAudios.assessment_complete);
            this.markEndIfNeeded();
            this.current_state = TUGAssessmentStates.test_complete;
            console.log("Finish line crossed!");

            ASSESSMENT_STATUSES.assessment_Completed = true;
            ASSESSMENT_STATUSES.assessment_successful_completion = true;

            let distance = 13 * 0.3048; // 10 feet in meters
            let gait_time =
              (this.end_time -
                this.start_time -
                this.total_calibration_error_time) /
              1000; // in seconds

            let gait_speed = (distance / gait_time).toFixed(2); // m/s
            console.log("Tug speed: ", gait_speed, "m/s");

            let response = this.build_tug_response(
              gait_speed,
              distance,
              gait_time,
            );

            console.log(response);
            this.append_sentry_message(
              "assessment completed - Finish line crossed!",
            );
            this.append_sentry_message(
              "Assessment response:" + JSON.stringify(response),
            );

            // Provide the response to the host page
            if (window["AssessmentPageReference"]) {
              window["AssessmentPageReference"].CompleteAssessment(response);
            } else {
              console.log("window['AssessmentPageReference'] not found");
            }
          }
          break;

        default:
          break;
      }
    } catch (error) {
      console.log(error);
    }

    if (ASSESSMENT_STATUSES.debug_calibration) {
      this.camera.video.play();
    }
  }

  /**
   * Helper function to check if patient is sitting
   * @param {Array} poses - Array of detected poses.
   * @returns {boolean} True if patient is sitting, otherwise false.
   */
  is_patient_sitting(poses) {
    if (!poses || poses.length === 0) return false;

    const pose = poses[0];
    const leftHip = pose.keypoints[23];
    const rightHip = pose.keypoints[24];
    const leftKnee = pose.keypoints[25];
    const rightKnee = pose.keypoints[26];
    const leftAnkle = pose.keypoints[27];
    const rightAnkle = pose.keypoints[28];

    // Vertical distances
    const leftHipKnee = leftKnee.y - leftHip.y;
    const leftKneeAnkle = leftAnkle.y - leftKnee.y;

    const rightHipKnee = rightKnee.y - rightHip.y;
    const rightKneeAnkle = rightAnkle.y - rightKnee.y;

    // Ratios
    const leftRatio = leftHipKnee / leftKneeAnkle;
    const rightRatio = rightHipKnee / rightKneeAnkle;

    // Debugging
    console.log({
      leftHip: leftHip.y,
      leftKnee: leftKnee.y,
      leftAnkle: leftAnkle.y,
      leftHipKnee,
      leftKneeAnkle,
      leftRatio,
    });

    // Standing: ratio ~ 0.8–1.2
    // Sitting: ratio ~ 0.2–0.5 (hip closer to knee)
    const sittingThreshold = 0.4;

    const leftSitting = leftRatio < sittingThreshold;
    const rightSitting = rightRatio < sittingThreshold;

    return leftSitting || rightSitting;
  }

  is_patient_standing(poses) {
    if (!poses || poses.length === 0) return false;

    const pose = poses[0];
    const leftHip = pose.keypoints[23];
    const rightHip = pose.keypoints[24];
    const leftKnee = pose.keypoints[25];
    const rightKnee = pose.keypoints[26];
    const leftAnkle = pose.keypoints[27];
    const rightAnkle = pose.keypoints[28];

    if (
      !leftHip ||
      !rightHip ||
      !leftKnee ||
      !rightKnee ||
      !leftAnkle ||
      !rightAnkle
    ) {
      return false;
    }

    const leftHipKnee = leftKnee.y - leftHip.y;
    const leftKneeAnkle = leftAnkle.y - leftKnee.y;
    const rightHipKnee = rightKnee.y - rightHip.y;
    const rightKneeAnkle = rightAnkle.y - rightKnee.y;

    if (leftKneeAnkle === 0 || rightKneeAnkle === 0) return false;

    const leftRatio = leftHipKnee / leftKneeAnkle;
    const rightRatio = rightHipKnee / rightKneeAnkle;

    // Standing tends to have larger hip-knee vs knee-ankle ratio
    const standingThreshold = 0.6;
    const leftStanding = leftRatio >= standingThreshold;
    const rightStanding = rightRatio >= standingThreshold;

    return leftStanding && rightStanding;
  }

  /**
   * Checks if any detected pose is both inside the bounding box and sitting.
   * Works with multi-pose format (not BlazePose).
   * @param {Array} poses - Array of detected poses.
   * @returns {boolean} True if any pose is in box and sitting, otherwise false.
   */
  is_patient_in_box_and_sitting(poses) {
    if (!poses || poses.length === 0) return false;
    for (let pose of poses) {
      // Check if pose is inside the bounding box
      const in_box = MultiPoseUtility.is_someone_in_box(
        [pose],
        this.patient_feet_box_x,
        [this.patient_feet_box_y[0], this.patient_feet_box_y[1]],
        this.camera,
      );
      if (in_box) {
        const wrapped = new MoveNetPoseWrapper(pose);
        if (this.sit_stand_processing.is_sitting(wrapped)) return true;
      }
    }
    return false;
  }

  /**
   * Checks if a single pose is sitting (multi-pose format, not BlazePose).
   * @param {object} pose - A single pose object.
   * @returns {boolean} True if sitting, otherwise false.
   */
  is_person_sitting(poses) {
    if (!poses || poses.length === 0) return false;

    console.log("Evaluating sitting for pose:", poses);

    // const pose = poses[0];
    // console.log("Evaluating sitting for pose:", pose);
    const leftHip = poses.keypoints[11];
    const rightHip = poses.keypoints[12];
    const leftKnee = poses.keypoints[13];
    const rightKnee = poses.keypoints[14];
    const leftAnkle = poses.keypoints[15];
    const rightAnkle = poses.keypoints[16];

    // Vertical distances (Y increases downward in MoveNet)
    const leftHipKnee = leftKnee.y - leftHip.y;
    const leftKneeAnkle = leftAnkle.y - leftKnee.y;

    const rightHipKnee = rightKnee.y - rightHip.y;
    const rightKneeAnkle = rightAnkle.y - rightKnee.y;

    // Ratios (hip–knee vs knee–ankle)
    const leftRatio = leftHipKnee / leftKneeAnkle;
    const rightRatio = rightHipKnee / rightKneeAnkle;

    // Threshold (tune this based on your data)
    // Standing → ratio ~ 0.8–1.2
    // Sitting  → ratio ~ 0.2–0.4
    const sittingThreshold = 0.7;

    const leftSitting = leftRatio < sittingThreshold;
    const rightSitting = rightRatio < sittingThreshold;

    console.log("LeftRatio:", leftRatio, "RightRatio:", rightRatio);
    console.log("LeftRatio:", leftSitting, "RightRatio:", rightSitting);

    return leftSitting || rightSitting;
  }

  /**
   * If a user left the start box prematurely, trigger a return to start modal,
   * revert states, and log the event.
   */
  handleEveryoneBehindPoint() {
    this.is_everyone_behind_the_point_counter += 1;
    if (this.is_everyone_behind_the_point_counter >= 10) {
      this.is_everyone_behind_the_point_counter = 0;

      // Stop everything: audio, voice, UI
      if (this.enabled_voice_listener) {
        stopiOSVoiceListening();
        this.enabled_voice_listener = false;
      }
      this.audio_player.stop();

      // Hide progress bar
      document.getElementById("hdnRemoveGaitDontWalk")?.click();

      // Play return-to-start audio and show modal
      this.audio_player.play(TugAudios.return_to_start_area);
      document.getElementById("hdnReturnToStartModalPopup").click();

      // Reset state for fresh restart from post-path-calibration
      this.shown_persistent_icon = false;
      this.hand_gesture_detected = false;
      this.voice_command_received = false;
      this.gesture_detector.reset();
      this.current_state = TUGAssessmentStates.MoveInsidebox;
      this.two_second_start_time = Date.now();
      this.append_sentry_message(
        "Someone started walking before hand raise detection",
      );
    }
  }

  /**
   * After detecting a start gesture, switch to single-pose estimation (e.g., BlazePose)
   * to isolate the main participant. This function also chooses which bounding box
   * to pass to the single-pose model for the best detection.
   * @param {Array} poses - The array of multi-pose keypoints from the current frame.
   */
  switch_to_blazepose(poses) {
    // The therapist's selection wins over the automatic pick (it already passed the start-box checks)
    let patient =
      this.patient_selection.selected_patient() ||
      MultiPoseUtility.return_patient(
        poses,
        this.patient_feet_box_x,
        [
          this.patient_feet_box_y[0] -
            Math.abs(
              this.calibration.start_point_cv.y - this.patient_feet_box_y[1],
            ),
          this.calibration.start_point_cv.y,
        ],
        this.gesture_detector.gesture_issued_by,
        this.camera,
      );
    if (!patient) {
      console.log(
        "Patient not found in the box - return_patient returning Null",
      );
      return;
    } else {
      // Defensive: check keypoints exist
      const left_hip = patient.keypoints[11];
      const right_hip = patient.keypoints[12];
      if (!left_hip || !right_hip) {
        console.log("Hip keypoints missing in patient pose");
        return;
      }
      let seed_rect = this.get_blazepose_bbox(patient);
      this.patient_pose = patient;

      console.log(`Seed Rect : ${JSON.stringify(seed_rect)}`);
      this.switch_to_single_pose_callback(seed_rect);

      this.append_sentry_message(
        "Switching to blaze pose with seed rect: " + JSON.stringify(seed_rect),
      );
    }
  }

  /**
   * Constructs a bounding box (seed_rect) for BlazePose from the current multi-pose detection.
   * @param {object} patient - The main patient pose object, including keypoints and bounding box.
   * @returns {object} The bounding box rectangle in normalized coordinates for BlazePose input.
   */
  get_blazepose_bbox(patient) {
    let left_hip = patient.keypoints[11];
    let right_hip = patient.keypoints[12];
    let spine_base = {
      x: (left_hip.x + right_hip.x) / 2,
      y: (left_hip.y + right_hip.y) / 2,
    };

    let bbox = patient.box;
    let box_size = 2 * Math.ceil(bbox.yMax * this.options.sh - spine_base.y);

    console.log("Seed box", bbox, spine_base, box_size);
    this.append_sentry_message(
      "Seed box: " +
        JSON.stringify(bbox) +
        " spine base: " +
        JSON.stringify(spine_base) +
        " box size: " +
        box_size,
    );

    let seed_rect = {
      xCenter: spine_base.x / this.camera.video.videoWidth,
      yCenter: spine_base.y / this.camera.video.videoHeight,
      width: box_size / this.camera.video.videoWidth,
      height: box_size / this.camera.video.videoHeight,
      rotation: 0.0,
    };
    return seed_rect;
  }

  /**
   * Creates a downloadable CSV file of the stored ankle and foot positions (Bird's Eye View).
   */
  download_ankle_locations() {
    let csvContent = "data:text/csv;charset=utf-8,";
    this.ankle_locations_bev.forEach(function (rowArray) {
      let row = rowArray.join(",");
      csvContent += row + "\r";
    });
    var encodedUri = encodeURI(csvContent);
    var link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "scase5_feet_n_ankle_locations.csv");
    document.body.appendChild(link);

    link.click(); // Download the CSV file
  }

  /**
   * Records the ankle positions of detected persons in Bird's Eye View for subsequent tug analysis.
   * @param {Array} poses - Array of pose objects from the current frame.
   */
  record_ankle_locations_BEV(poses) {
    if (this.persons_detected(poses)) {
      for (let i = 0; i < poses.length; i++) {
        let left_ankle = poses[i].keypoints[this.LEFT_ANKLE_IDX];
        let right_ankle = poses[i].keypoints[this.RIGHT_ANKLE_IDX];
        let left_foot = poses[i].keypoints[this.LEFT_FOOT_IDX];
        let right_foot = poses[i].keypoints[this.RIGHT_FOOT_IDX];
        let feet = this.get_ankle_locations_in_birds_eye_view(poses[i]);
        let feet_data = new FeetData(feet.left, feet.right);

        console.log("this.ankle_locations_bev", this.ankle_locations_bev);
        // Save both the Bird's Eye View and camera coordinate data
        this.feet_locations.push(feet_data);
        this.feet_locations_cam.push(new FeetData(left_foot, right_foot));
        this.ankle_locations_bev.push([
          Date.now(),
          feet.left.x,
          feet.left.y,
          feet.right.x,
          feet.right.y,
          left_foot.x,
          left_foot.y,
          right_foot.x,
          right_foot.y,
          left_ankle.x,
          left_ankle.y,
          right_ankle.x,
          right_ankle.y,
          left_foot.score,
          right_foot.score,
          left_ankle.score,
          right_ankle.score,
        ]);
      }
    }
  }

  /**
   * Detects if a second person has crossed or is crossing in front of/behind the patient.
   * Simplistic check based on shoulder distances and movement over frames.
   * @param {Array} poses - Detected multi-person pose array.
   * @returns {boolean} True if second person crossing is detected, false otherwise.
   */
  second_person_crossing_detection(poses) {
    if (this.persons_detected(poses)) {
      for (let i = 0; i < poses.length; i++) {
        let left_shoulder_value = poses[i].keypoints[this.LEFT_SHOULDER_IDX];
        let right_shoulder_value = poses[i].keypoints[this.RIGHT_SHOULDER_IDX];
        this.shoulder_differernce = Math.abs(
          left_shoulder_value.x - right_shoulder_value.x,
        );
        let shoulder_mean =
          (left_shoulder_value.x + right_shoulder_value.x) / 2;
        if (this.prev_index !== 0) {
          if (
            this.shoulder_differernce < this.prev_index * 0.6 ||
            shoulder_mean < this.right_shoulder ||
            shoulder_mean > this.left_boundary
          ) {
            console.log(
              "assessment-error: shoulder width reduced or moved. ",
              this.shoulder_differernce < this.prev_index * 0.6,
              " ",
              this.right_shoulder,
              " ",
              this.left_boundary,
              " ",
              shoulder_mean,
            );
            return true;
          }
        }
        this.prev_index = this.shoulder_differernce;
        this.right_shoulder = right_shoulder_value.x;
        this.left_boundary = left_shoulder_value.x;
      }
      return false;
    }
  }

  /**
   * Builds the final tug assessment response object.
   * @param {number} tug_speed_wrong - A fallback speed calculation if the more precise method is not available.
   * @param {number} distance - The distance walked in meters.
   * @param {number} gait_time - The time taken in seconds.
   * @returns {object} The structured tug assessment response.
   */
  build_tug_response(gait_speed_wrong, distance, gait_time) {
    let { step_count, gait_speed } = this.step_detector.count_steps(
      this.feet_locations,
      this.feet_locations_cam,
      this.calibration.depthData,
    );

    const gait_speed_final = gait_speed_wrong;

    // Suspicious step counts trigger alert emails
    if (step_count > 25 || step_count <= 2) {
      this.email_generation_stepcount(step_count);
      this.append_sentry_message(
        `Tug Unusual Step Count Detected. Detected steps: ${step_count}`,
      );
    }
    // Suspicious tug speed triggers alert emails
    if (gait_speed > 2) {
      this.email_generation_gaitspeed(gait_speed);
      this.append_sentry_message(`Unusual Tug Speed: ${gait_speed}`);
    }

    // Robust end/start handling
    const safeEnd = this.end_time ? this.end_time : Date.now();
    const safeStart = this.start_time;
    const total_time_sec = Math.max(0, (safeEnd - safeStart) / 1000);

    return {
      test_type: "tug",
      start_time: safeStart.toString(),
      end_time: safeEnd.toString(),
      timing: {
        total: total_time_sec * 1000, // in mili seconds
      },
      gait: {
        fall_risk: calculateFallRisk(gait_speed_final, getPatientAge()).toFixed(
          2,
        ),
        tug_speed: gait_speed_final,
        distance: distance,
        ambulation_time: gait_time,
        velocity: gait_speed_final * 100,
        mean_normalized_velocity: 175.18,
        number_of_steps: step_count,
        cadence: 60 * (step_count / gait_time),
        used_assistive_device: "False",
      },
    };
  }

  /**
   * Wrapper for the calibration function to get a point in Bird's Eye View.
   * @param {object} point - Keypoint with x,y fields in camera coordinates.
   * @returns {object} Transformed point in Bird's Eye View coordinates.
   */
  get_birds_eye_view_point(point) {
    return this.calibration.get_birds_eye_view_point(point);
  }

  /**
   * Checks if at least one person pose is detected.
   * @param {Array} poses - Array of pose objects from the current frame.
   * @returns {boolean} True if poses are present, otherwise false.
   */
  persons_detected(poses) {
    if (!poses || poses.length === 0) {
      return false;
    }
    return true;
  }

  /**
   * Retrieves the ankle locations of a single pose in Bird's Eye View.
   * @param {object} pose - A single pose object containing keypoints.
   * @returns {{left: object, right: object}} The left and right foot coordinates in Bird's Eye View.
   */
  get_ankle_locations_in_birds_eye_view(pose) {
    let left_ankle = pose.keypoints[this.LEFT_FOOT_IDX];
    let right_ankle = pose.keypoints[this.RIGHT_FOOT_IDX];
    let warped_left_ankle = this.get_birds_eye_view_point(left_ankle);
    let warped_right_ankle = this.get_birds_eye_view_point(right_ankle);

    return {
      left: warped_left_ankle,
      right: warped_right_ankle,
    };
  }

  /**
   * Sets references for the patient's shoulder region, used to detect crossing or interference.
   * @param {Array} poses - Array of detected poses. Should primarily contain the patient's pose.
   */
  setting_patient_region(poses) {
    this.left_shoulder_bound = poses[0].keypoints[this.LEFT_SHOULDER_IDX];
    this.right_shoulder_bound = poses[0].keypoints[this.RIGHT_SHOULDER_IDX];
    this.shoulder_diff = Math.abs(
      this.left_shoulder_bound.x - this.right_shoulder_bound.x,
    );
    this.left_shoulder_bound.x =
      this.left_shoulder_bound.x + this.shoulder_diff;
    this.right_shoulder_bound.x =
      this.right_shoulder_bound.x - this.shoulder_diff;

    if (this.left_shoulder_bound.x > this.videoWidth) {
      this.left_shoulder_bound.x = this.videoWidth;
    }
    if (this.right_shoulder_bound.x < 0) {
      this.right_shoulder_bound.x = 0;
    }
  }

  /**
   * @returns {boolean} True if the finish line has been crossed, false otherwise.
   */
  is_finish_line_crossed() {
    return this.finish_line_crossed;
  }

  /**
   * @returns {boolean} True if the start line has been crossed, false otherwise.
   */
  is_start_line_crossed() {
    return this.start_line_crossed;
  }

  /**
   * Detect if a non-patient crossing scenario might have occurred based on sudden large movements in Bird's Eye View.
   * @param {Array} poses - Array of detected poses.
   * @returns {boolean} True if crossing is detected, otherwise false.
   */
  bird_eye_view_patient_crossing(poses) {
    if (this.persons_detected(poses)) {
      for (let i = 0; i < poses.length; i++) {
        let left_ankle = poses[i].keypoints[this.LEFT_ANKLE_IDX];
        let right_ankle = poses[i].keypoints[this.RIGHT_ANKLE_IDX];

        let jump_limit =
          Math.abs(
            this.calibration.stop_point.y - this.calibration.start_point.y,
          ) * 0.65;

        let warped_left_ankle =
          this.calibration.get_birds_eye_view_point(left_ankle);
        let warped_right_ankle =
          this.calibration.get_birds_eye_view_point(right_ankle);

        // Compare with previous frame positions
        if (this.prev_left_ankle_value !== 0) {
          if (
            Math.abs(this.prev_left_ankle_value - warped_left_ankle.y) >=
              jump_limit ||
            Math.abs(this.prev_right_ankle_value - warped_right_ankle.y) >=
              jump_limit
          ) {
            console.log(
              "assessment-error: bird eye view - patient crossing alert",
              Math.abs(this.prev_left_ankle_value - warped_left_ankle.y),
              " ",
              Math.abs(this.prev_right_ankle_value - warped_right_ankle.y),
              " ",
              jump_limit,
            );
            return true;
          }
        }
        if (warped_left_ankle.y > 0 && warped_right_ankle.y > 0) {
          this.prev_left_ankle_value = warped_left_ankle.y;
          this.prev_right_ankle_value = warped_right_ankle.y;
        }
      }
    }
    return false;
  }

  /**
   * Checks if the patient is near the stop line by comparing the ankles in Bird's Eye View
   * to the stop point within a small threshold.
   * @param {Array} poses - Array of detected poses.
   */
  patient_near_stopline(poses) {
    if (this.persons_detected(poses)) {
      for (let i = 0; i < poses.length; i++) {
        let left_ankle = poses[i].keypoints[this.LEFT_ANKLE_IDX];
        let right_ankle = poses[i].keypoints[this.RIGHT_ANKLE_IDX];
        let limit =
          Math.abs(
            this.calibration.stop_point.y - this.calibration.start_point.y,
          ) * 0.15;
        let warped_left_ankle =
          this.calibration.get_birds_eye_view_point(left_ankle);
        let warped_right_ankle =
          this.calibration.get_birds_eye_view_point(right_ankle);

        // If close to the stop line, set a flag
        if (
          Math.abs(this.calibration.stop_point.y - warped_left_ankle.y) <=
            limit ||
          Math.abs(this.calibration.stop_point.y - warped_right_ankle.y) <=
            limit
        ) {
          console.log("Patient is almost at the stop line");
          this.isPatientNearStopLine = true;
        }
      }
    }
  }

  /**
   * True while the patient can be changed with the remote: from when the start box is shown until
   * the start is accepted.
   * @returns {boolean}
   */
  is_patient_selection_open() {
    return (
      [
        TUGAssessmentStates.MoveInsidebox,
        TUGAssessmentStates.SitToStart,
        TUGAssessmentStates.ready_to_start_test,
      ].includes(this.current_state) &&
      this.patient_feet_box_x.length >= 2 &&
      this.patient_feet_box_y.length >= 2 &&
      !this.hand_gesture_detected &&
      !this.voice_command_received &&
      !ASSESSMENT_STATUSES.exit_assessment_modal &&
      !ASSESSMENT_STATUSES.calibration_error_state
    );
  }

  /**
   * Patient selection from outside the frame loop (BLE remote UP/DOWN): moves the patient to the
   * next detected person on screen. They still have to be in the start box for the test to start.
   * @param {number} direction - +1 for the next person to the right, -1 for the next to the left.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if the selection changed.
   */
  cyclePatient(direction, source) {
    if (!this.is_patient_selection_open()) return false;

    const selected = this.patient_selection.cycle(direction);
    if (!selected) return false;

    this.patient_id = selected.id;
    this.append_sentry_message(`Patient selected by ${source}: pose id ${selected.id}`);
    return true;
  }

  /**
   * Start trigger from outside the frame loop (BLE remote START_STOP). Accepted once ready to start
   * with the selected participant seated in the box, also while the raise-hand prompt is still
   * playing (it is cut short).
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if the trigger was accepted.
   */
  receiveStartTrigger(source) {
    // A remote start needs a participant selected with UP/DOWN (a hand raise or voice start doesn't)
    if (
      !this.patient_selection.selected_patient() ||
      this.current_state !== TUGAssessmentStates.ready_to_start_test ||
      this.voice_command_received ||
      // update() ignores the first 2 s of this state
      Date.now() - this.two_second_start_time <= 2000 ||
      // The selected participant seated in the box (the same debounce that enables voice)
      this.patient_seated_confirm_frames < 4
    ) {
      return false;
    }
    // Same flag as voice START, so update() starts the test through the existing path
    this.voice_command_received = true;
    // Cut the prompt short: the remote may start while it is still playing
    this.audio_player.stop();
    if (this.enabled_voice_listener) {
      stopiOSVoiceListening();
      this.enabled_voice_listener = false;
    }
    this.append_sentry_message(`Start triggered by ${source}`);
    return true;
  }

  /**
   * Stop trigger from outside the frame loop (BLE remote START_STOP). Accepted only while the
   * test is running (start triggered, not yet finished or errored). Freezes update() so nothing
   * more is measured while the caller shows the exit choice.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if the trigger was accepted.
   */
  receiveStopTrigger(source) {
    const running = [
      TUGAssessmentStates.start_gesture_detected,
      TUGAssessmentStates.test_in_progress,
      TUGAssessmentStates.ReturnToStart,
    ].includes(this.current_state);
    if (
      !running ||
      ASSESSMENT_STATUSES.exit_assessment_modal ||
      ASSESSMENT_STATUSES.calibration_error_state
    ) {
      return false;
    }
    ASSESSMENT_STATUSES.exit_assessment_modal = true;
    this.append_sentry_message(`Assessment stopped by ${source}`);
    return true;
  }

  /**
   * NEXT trigger from outside the frame loop (BLE remote NEXT). TUG has a single phase, so before
   * the test starts NEXT leaves the assessment. Freezes update() while the caller navigates away.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {"exit"|null} What the caller should do, or null if NEXT doesn't apply now.
   */
  receiveNextTrigger(source) {
    const before_start = [
      TUGAssessmentStates.PathwayCalibration,
      TUGAssessmentStates.assessment_started,
      TUGAssessmentStates.calculatingDepth,
      TUGAssessmentStates.depthCalculated,
      TUGAssessmentStates.MoveInsidebox,
      TUGAssessmentStates.SitToStart,
      TUGAssessmentStates.ready_to_start_test,
    ].includes(this.current_state);
    if (
      !before_start ||
      this.voice_command_received ||
      this.hand_gesture_detected ||
      ASSESSMENT_STATUSES.exit_assessment_modal ||
      ASSESSMENT_STATUSES.calibration_error_state
    ) {
      return null;
    }
    ASSESSMENT_STATUSES.exit_assessment_modal = true;
    this.append_sentry_message(`Assessment exited by ${source} (NEXT before start)`);
    return "exit";
  }

  /**
   * Appends a message to the Sentry log with a timestamp.
   * @param {string} message - The message to log.
   */
  append_sentry_message(message) {
    let event = new Date();
    ASSESSMENT_STATUSES.sentry_message =
      ASSESSMENT_STATUSES.sentry_message +
      "\n" +
      event.toISOString() +
      ": " +
      message;

    // Additional time logging for calibration or gestures
    if (
      message === "Calibration Started" ||
      message === "Hand Raised Modal is displayed."
    ) {
      this.sentry_start_time = Date.now();
    } else if (message === "Calibration complete!") {
      let diff = (Date.now() - this.sentry_start_time) / 1000;
      ASSESSMENT_STATUSES.sentry_message =
        ASSESSMENT_STATUSES.sentry_message +
        "Calibration time: " +
        diff +
        " seconds";
    } else if (message === "Start (Hand raised) gesture issued!") {
      let diff = (Date.now() - this.sentry_start_time) / 1000;
      ASSESSMENT_STATUSES.sentry_message =
        ASSESSMENT_STATUSES.sentry_message +
        ", Hand Raise gesture detection time: " +
        diff +
        " seconds";
    }
  }

  /**
   * Checks if any person pose is within a central path region, indicating the path is blocked.
   * @param {Array} poses - Array of multi-person pose objects.
   * @returns {boolean} True if the path is clear (no one inside it), false if blocked.
   */
  path_checking(poses) {
    if (!poses || !Array.isArray(poses)) {
      // If poses isn't valid, assume the path is clear
      return true;
    }

    // Define the bounding box for the path
    const xMin = this.options.sw / 2 - 150;
    const xMax = this.options.sw / 2 + 150;
    const yMin = 1200;
    const yMax = this.camera.video.videoHeight;

    let fillxMin = this.camera.scale_to_fit({ x: xMin, y: 0 }).x;
    let fillyMin = this.camera.scale_to_fit({ x: 0, y: yMin }).y;
    let fillxMax = this.camera.scale_to_fit({ x: xMax, y: 0 }).x;
    let fillyMax = this.camera.scale_to_fit({ x: 0, y: yMax }).y;

    // For each person, check if any keypoint is in the path bounding box
    for (let person of poses) {
      console.log("person detected");
      if (person.score > 0.25) {
        for (let kp of person.keypoints) {
          if (kp.score > 0.25) {
            let scaledX = this.camera.scale_to_fit({ x: kp.x, y: 0 }).x;
            let scaledY = this.camera.scale_to_fit({ x: 0, y: kp.y }).y;
            if (
              scaledX >= fillxMin &&
              scaledX <= fillxMax &&
              scaledY >= fillyMin &&
              scaledY <= fillyMax
            ) {
              console.log("found another person in the path");
              return false;
            }
          }
        }
      }
    }

    // No one is in the path
    return true;
  }

  /**
   * Based on a stored angle value, sets an "extended start point" and associated line
   * for path checking or bounding box placement.
   */
  get_start_extended_points() {
    const y_axis = parseInt(ASSESSMENT_STATUSES.current_angle);

    const angleToYMap = {
      88: 1085,
      89: 1095,
      90: 1105,
      91: 1120,
      92: 1130,
    };

    const y_extended = angleToYMap[y_axis];
    this.calibration.start_point_extended_cv = new cv.Point(
      this.options.sw / 2,
      y_extended,
    );

    this.calibration.start_line_extended_cv = {
      x1: Math.min(this.calibration.start_point_extended_cv.x - 50, 0),
      y1: this.calibration.start_point_extended_cv.y,
      x2: Math.max(
        this.calibration.start_point_extended_cv.x + 50,
        this.options.sw,
      ),
      y2: this.calibration.start_point_extended_cv.y,
    };
  }

  /**
   * Draws a "path clearance" box over the canvas to visualize the region we check for blockages.
   * Animates a color fill from bottom to top.
   */
  draw_PathClearanceBox() {
    const c = document.getElementById("output");
    const ctx = c.getContext("2d");
    ctx.lineWidth = 3;

    // Create a vertical gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, c.height);
    gradient.addColorStop(0, "rgba(0, 255, 255, 0.2)");
    gradient.addColorStop(1, "rgba(255, 0, 255, 0.2)");

    ctx.fillStyle = gradient;
    ctx.strokeStyle = "magenta";

    const xMin = this.options.sw / 2 - 150;
    const xMax = this.options.sw / 2 + 150;
    const yMin = 1200;
    const yMax = this.camera.video.videoHeight;

    let fillxMin = this.camera.scale_to_fit({ x: xMin, y: 0 }).x;
    let fillyMin = this.camera.scale_to_fit({ x: 0, y: yMin }).y;
    let fillxMax = this.camera.scale_to_fit({ x: xMax, y: 0 }).x;
    let fillyMax = this.camera.scale_to_fit({ x: 0, y: yMax }).y;

    const ctx2 = c.getContext("2d");
    ctx2.beginPath();
    ctx2.strokeStyle = "yellow";
    ctx2.lineWidth = 3;
    ctx2.fillStyle = "rgba(255,255,0,0.2)";
    ctx2.fillRect(fillxMin, fillyMin, fillxMax - fillxMin, fillyMax - fillyMin);
    ctx2.rect(fillxMin, fillyMin, fillxMax - fillxMin, fillyMax - fillyMin);
    ctx2.stroke();
    ctx2.closePath();

    if (this.current_y < fillyMin) {
      this.current_y = fillyMax;
      this.old_y = fillyMax;
    }

    // Basic animation that moves upward
    const animate = () => {
      if (this.current_y < fillyMax) {
        ctx.clearRect(fillxMin, this.old_y - 5, fillxMax - fillxMin, 10);
      }
      ctx.beginPath();
      ctx.fillRect(
        fillxMin,
        this.current_y,
        fillxMax - fillxMin,
        fillyMax - this.current_y,
      );
      ctx.rect(
        fillxMin,
        this.current_y,
        fillxMax - fillxMin,
        fillyMax - this.current_y,
      );
      ctx.stroke();
      ctx.closePath();

      this.old_y = this.current_y;
      if (this.current_y > fillyMin) {
        this.current_y -= 2;
        requestAnimationFrame(animate);
      }
    };
    animate();
  }

  /**
   * Captures a frame (PNG) from the video, uploads it to S3,
   * then calls an external API to generate depth data. If it fails multiple times,
   * it raises an error, cancels the assessment, and plays an interruption audio.
   */
  async uploadImageToS3AndCallAPI() {
    try {
      let PatientId = localStorage.getItem("patientServerID");
      let image_name = `images/scene_depth_input_${PatientId}.png`;
      const s3Bucket = S3_BUCKET;
      const s3Key = `depths/scene_depth_${PatientId}.npy`;

      // Capture current frame to offscreen canvas
      this.offscreen_canvas.width = this.camera.video.videoWidth;
      this.offscreen_canvas.height = this.camera.video.videoHeight;
      this.offscreen_canvas_ctx.drawImage(
        this.camera.video,
        0,
        0,
        this.camera.video.videoWidth,
        this.camera.video.videoHeight,
      );

      const imageUrl = this.offscreen_canvas.toDataURL("image/png");

      // Convert base64 to binary
      const base64Data = imageUrl.replace(/^data:image\/png;base64,/, "");
      const binaryData = atob(base64Data);
      const binaryBuffer = new Uint8Array(binaryData.length);

      for (let i = 0; i < binaryData.length; i++) {
        binaryBuffer[i] = binaryData.charCodeAt(i);
      }

      // Upload the screenshot to S3
      const params = {
        Bucket: s3Bucket,
        Key: image_name,
        Body: binaryBuffer,
        ContentEncoding: "base64",
        ContentType: "image/png",
      };
      await s3Client.send(new PutObjectCommand(params));
      console.log("Successfully uploaded data to S3");
      this.append_sentry_message("Successfully uploaded data to S3");

      const apiURL = API_URL;
      const postData = {
        image_path: image_name,
        model_selection: "vit-large",
        PatientId: PatientId,
        fx: 740,
        fy: 740,
      };

      // Retry logic for external API call
      let attempts = 3;
      while (attempts > 0) {
        try {
          const controller = new AbortController();
          const id = setTimeout(() => controller.abort(), 15000); // 15s timeout

          const response = await fetch(apiURL, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(postData),
            signal: controller.signal,
          });

          clearTimeout(id);

          if (!response.ok) {
            this.append_sentry_message(
              `HTTP error! Status: ${response.status}`,
            );
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          console.log("success from API");
          this.append_sentry_message("API call successful");

          // Load the generated .npy file from S3 into calibration
          this.is_uploaded = await this.calibration.get_depth_data(
            s3Bucket,
            s3Key,
          );
          break;
        } catch (error) {
          console.error("Error in API call:", error);
          this.append_sentry_message("Error in API call");
          attempts--;
          if (attempts === 0) {
            console.error("API request failed after multiple attempts.");
            this.append_sentry_message(
              "API request failed after multiple attempts.",
            );
            // Stop voice listening on API failure
            if (this.enabled_voice_listener) {
              stopiOSVoiceListening();
              this.enabled_voice_listener = false;
            }
            this.current_state = TUGAssessmentStates.test_complete;
            document.getElementById("hdnRemoveGaitDontWalk")?.click();
            document.getElementById("hdnAssessementErrorPopup").click();
            ASSESSMENT_STATUSES.assessment_Completed = true;
            localStorage.removeItem("modalDisplayed");
            this.audio_player.play(TugAudios.assessment_interrupted);
            return;
          } else {
            console.log(`Retrying... attempts left: ${attempts}`);
            this.append_sentry_message(
              `Retrying... attempts left: ${attempts}`,
            );
          }
        }
      }
    } catch (err) {
      console.log("Error uploading data: ", err);
      this.append_sentry_message("Error uploading data to S3 bucket");
      // Stop voice listening on S3 upload error
      if (this.enabled_voice_listener) {
        stopiOSVoiceListening();
        this.enabled_voice_listener = false;
      }
      this.current_state = TUGAssessmentStates.test_complete;
      document.getElementById("hdnRemoveGaitDontWalk")?.click();
      document.getElementById("hdnAssessementErrorPopup").click();
      ASSESSMENT_STATUSES.assessment_Completed = true;
      localStorage.removeItem("modalDisplayed");
      this.audio_player.play(TugAudios.assessment_interrupted);
      return;
    }
  }

  /**
   * TODO: Refactor into a shared BoundingBoxRenderer utility class.
   * The drawing logic (roundRect, fill color, shoe print image, dotted border) is
   * duplicated across gait.js, tug.js, mCTSIB.js, and sitStandProcessing.js.
   * Each assessment calculates box coordinates differently (gait uses start_line_extended_cv,
   * TUG uses create_patient_box_at_distance with 3ft/13ft, mCTSIB uses 6ft, sitStandProcessing
   * uses fixed margins), but the rendering is identical. A shared class with a
   * draw(ctx, x, y, width, height, isPatientInBox) method would eliminate this duplication.
   */
  draw_PatientFeetBox(poses) {
    var c = document.getElementById("output");

    // const threeFeetMeters = 13 * 0.3048;
    // this.calibration.get_line_at_distance(threeFeetMeters);

    if (!this.calibration.complete()) {
      const threeFeetMeters = 3 * 0.3048;
      this.calibration.get_line_at_distance(threeFeetMeters);
      const thirteenFeetMeters = 13 * 0.3048;
      this.calibration.get_line_at_distance(thirteenFeetMeters);
    }

    const thirteenFeetMeters = 13 * 0.3048;
    const threeFeetMeters = 3 * 0.3048;
    const box = this.calibration.create_patient_box_at_distance(
      this.current_state === TUGAssessmentStates.test_in_progress
        ? threeFeetMeters
        : thirteenFeetMeters,
    );
    console.log("feetBox:", box);

    if (!box) {
      this.notEnoughSpace = true;
      return; // Don't draw if box is not found
    }

    console.log("feetBox:", box);
    let rect_start_x = Math.floor(c.width * this.side_margin) - 50;
    let scale_to_fit_the_point = this.camera.scale_to_fit_the_point(
      box.x1 + 100,
      box.y1,
    );
    let rect_start_y = Math.floor(scale_to_fit_the_point.y);
    let rect_width = c.width * (1 - 2 * this.side_margin) + 100;
    let rect_height = this.camera.scale_to_fit_the_point(
      box.x1 + 100,
      box.y1 - FEET_BOX_LENGTH,
    ).y;
    rect_height = -Math.abs(scale_to_fit_the_point.y - rect_height);

    this.patient_feet_box_x[0] = rect_start_x;
    this.patient_feet_box_x[1] = rect_start_x + rect_width;
    this.patient_feet_box_y[0] = box.y1 - FEET_BOX_LENGTH;
    this.patient_feet_box_y[1] = box.y1;

    this.ctx.beginPath();
    this.ctx.lineWidth = 5;

    // Buffer variable -> 30% above & below the box's middle point
    let patient_inner_buffer = Math.abs(
      (this.patient_feet_box_y[0] - this.patient_feet_box_y[1]) * 0.2,
    );

    let patient_in_box = MultiPoseUtility.is_someone_in_box(
      poses,
      this.patient_feet_box_x,
      [
        this.patient_feet_box_y[0] + patient_inner_buffer,
        this.patient_feet_box_y[1] - patient_inner_buffer,
      ],
      this.camera,
    );

    let single_patient_in_box = false;
    if (
      this.current_state === TUGAssessmentStates.ReturnToStart ||
      this.current_state === TUGAssessmentStates.test_in_progress
    ) {
      single_patient_in_box = SinglePoseUtil.is_someone_in_box(
        poses,
        this.patient_feet_box_x,
        [this.patient_feet_box_y[0], this.patient_feet_box_y[1]],
        this.camera,
      );
    }
    // Fill color indicates whether someone is present in the box
    if (patient_in_box || single_patient_in_box) {
      this.ctx.fillStyle = "rgba(0,128,0,0.75)";
    } else {
      this.ctx.fillStyle = "rgba(150,75,0,0.75)";
    }

    this.ctx.roundRect(rect_start_x, rect_start_y, rect_width, rect_height, 20);
    this.ctx.fill();

    // Draw shoe print image centered in the box
    if (this.shoe_print_loaded) {
      const imgAspect = this.shoe_print_img.width / this.shoe_print_img.height;
      const boxAbsHeight = Math.abs(rect_height);
      const imgHeight = boxAbsHeight * 0.8;
      const imgWidth = imgHeight * imgAspect;
      const imgX = rect_start_x + (rect_width - imgWidth) / 2;
      const imgY = rect_start_y + (rect_height - imgHeight) / 2;
      this.ctx.globalAlpha = 0.85;
      this.ctx.drawImage(this.shoe_print_img, imgX, imgY, imgWidth, imgHeight);
      this.ctx.globalAlpha = 1.0;
    }

    // Dotted border lines
    this.ctx.lineWidth = 5;
    this.ctx.setLineDash([10, 5]);
    this.ctx.strokeStyle = "rgba(0,0,0,0.75)";
    this.ctx.stroke();
    this.ctx.setLineDash([]);
  }

  /**
   * Checks for sudden, large changes in shoulder positions (indicative of flicker or detection error).
   * Logs an alert if values deviate drastically between frames.
   * @param {Array} poses - Array of detected poses in the current frame.
   */
  flickerDetection(poses) {
    if (this.persons_detected(poses)) {
      for (let i = 0; i < poses.length; i++) {
        let left_shoulder = poses[i].keypoints[11];
        let right_shoulder = poses[i].keypoints[12];

        let warped_left_shoulder = left_shoulder;
        let warped_right_shoulder = right_shoulder;

        if (
          this.prev_right_shoulder_value.y != null &&
          this.prev_right_shoulder_value.x != null &&
          this.prev_left_shoulder_value.y != null &&
          this.prev_left_shoulder_value.x != null
        ) {
          let right_magnitude = Math.sqrt(
            Math.pow(this.prev_right_shoulder_value.y - right_shoulder.y, 2) +
              Math.pow(this.prev_right_shoulder_value.x - right_shoulder.x, 2),
          );
          let left_magnitude = Math.sqrt(
            Math.pow(this.prev_left_shoulder_value.y - left_shoulder.y, 2) +
              Math.pow(this.prev_left_shoulder_value.x - left_shoulder.x, 2),
          );

          // If magnitude is large, consider it a flicker
          if (right_magnitude >= 300 || left_magnitude >= 300) {
            console.log("Sudden Change in the Pose values are observed.");
            this.append_sentry_message(
              "Sudden Change in the Pose values are observed.",
            );
            this.flickerStatus = true;
          }
        }

        // Store current frame's shoulder for next iteration
        this.prev_right_shoulder_value.x = warped_right_shoulder.x;
        this.prev_right_shoulder_value.y = warped_right_shoulder.y;
        this.prev_left_shoulder_value.x = warped_left_shoulder.x;
        this.prev_left_shoulder_value.y = warped_left_shoulder.y;
      }
    }
  }

  /**
   * Sends an email alert when a suspiciously high or low step count is detected.
   * @param {number} step_count - The number of steps detected by the step detector.
   */
  email_generation_stepcount = async (step_count) => {
    console.log("step counts:", step_count);
    let subject = this.sentry_prefix + ": Steps count looks unusuall.";
    let body = this.sentry_prefix + "\n";
    body = body + "Steps count: " + step_count + "\n";

    try {
      await SendExceptionEmail({
        subject: subject,
        body: body,
      })
        .then(async function (response) {
          console.log("Zendesk Email: ", response.data);
        })
        .catch((error) => {
          console.error(error);
        });
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Sends an email alert when a suspiciously high tug speed is detected.
   * @param {number} tug_speed - The detected tug speed in m/s.
   */
  email_generation_gaitspeed = async (tug_speed) => {
    console.log("tug speed:", tug_speed);
    let subject = this.sentry_prefix + ": Tug speed looks unusuall.";
    let body = this.sentry_prefix + "\n";
    body = body + "Tug speed: " + tug_speed + "\n";

    try {
      await SendExceptionEmail({
        subject: subject,
        body: body,
      })
        .then(async function (response) {
          console.log("Zendesk Email: ", response.data);
        })
        .catch((error) => {
          console.error(error);
        });
    } catch (error) {
      console.error(error);
    }
  };

  has_patient_turned_and_moved(poses) {
    if (!poses || poses.length === 0) return false;
    const pose = poses[0];
    // Shoulders
    const leftShoulder = pose.keypoints[this.LEFT_SHOULDER_IDX];
    const rightShoulder = pose.keypoints[this.RIGHT_SHOULDER_IDX];
    // Hips
    const leftHip = pose.keypoints[23] || pose.keypoints[11];
    const rightHip = pose.keypoints[24] || pose.keypoints[12];
    // Ankles
    const leftAnkle = pose.keypoints[this.LEFT_ANKLE_IDX];
    const rightAnkle = pose.keypoints[this.RIGHT_ANKLE_IDX];
    // Check keypoint validity
    if (!leftShoulder || !rightShoulder || !leftHip || !rightHip) return false;
    // Calculate shoulder angle
    const dxS = rightShoulder.x - leftShoulder.x;
    const dyS = rightShoulder.y - leftShoulder.y;
    const angleS = Math.abs(Math.atan2(dyS, dxS) * (180 / Math.PI));
    // Calculate hip angle
    const dxH = rightHip.x - leftHip.x;
    const dyH = rightHip.y - leftHip.y;
    const angleH = Math.abs(Math.atan2(dyH, dxH) * (180 / Math.PI));
    // Store initial orientation if not set
    if (this.initial_shoulder_angle === undefined) {
      this.initial_shoulder_angle = angleS;
      this.initial_hip_angle = angleH;
    }
    // Check for flip (difference close to 180°)
    const flipped_shoulder =
      Math.abs(angleS - this.initial_shoulder_angle) > 150;
    const flipped_hip = Math.abs(angleH - this.initial_hip_angle) > 150;
    // Check if ankles are moving towards the chair (greater y than stop point)
    const moving_towards_chair =
      leftAnkle &&
      rightAnkle &&
      leftAnkle.y > this.calibration.stop_point_cv.y - 300 &&
      rightAnkle.y > this.calibration.stop_point_cv.y - 300;
    return (!flipped_shoulder || !flipped_hip) && moving_towards_chair;
  }
}

/**
 * Initializes Sentry logging for Tug-based assessments.
 */
export async function tugSentryLogging() {
  SentryLogging("TUG");
}
